// PDF export for the Clinical Analysis (admin / coordinator view).
// Captures recharts SVG charts via html-to-image and composes a printable
// A4 portrait PDF with the same institutional branding as the care program PDF.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toPng } from "html-to-image";
import { LEVELS } from "@/lib/priority";

const BRAND = {
  navy: [30, 58, 138],
  blue: [29, 78, 216],
  light: [239, 246, 255],
  ink: [15, 23, 42],
  muted: [100, 116, 139],
  border: [203, 213, 225],
  white: [255, 255, 255],
};

let _logoCache = null;
async function loadLogo() {
  if (_logoCache !== null) return _logoCache;
  try {
    const res = await fetch("/assets/logo.svg", { cache: "force-cache" });
    if (!res.ok) throw new Error("logo fetch failed");
    const svgText = await res.text();
    const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const dataUrl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const size = 1024;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, size, size);
          ctx.drawImage(img, 0, 0, size, size);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };
      img.src = url;
    });
    _logoCache = dataUrl;
    return dataUrl;
  } catch (e) {
    console.warn("logo load failed", e);
    _logoCache = "";
    return "";
  }
}

function drawHeader(doc, logoDataUrl, subtitle) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, w, 30, "F");
  doc.setFillColor(...BRAND.blue);
  doc.rect(0, 0, w / 2, 30, "F");

  if (logoDataUrl) {
    doc.setFillColor(...BRAND.white);
    doc.roundedRect(10, 4, 22, 22, 3, 3, "F");
    try { doc.addImage(logoDataUrl, "PNG", 11, 5, 20, 20, undefined, "FAST"); } catch (e) { /* ignore */ }
  }

  doc.setTextColor(...BRAND.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("CIUDAD ENFERMERIA", 37, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("San Luis Potosi", 37, 19);
  doc.setFontSize(9);
  doc.text(subtitle || "Analisis clinico del paciente", 37, 24);

  const now = new Date();
  const dateStr = now.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  const timeStr = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: true });
  doc.setTextColor(...BRAND.white);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("GENERADO", w - 12, 9, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(dateStr, w - 12, 17, { align: "right" });
  doc.setFontSize(12);
  doc.text(timeStr, w - 12, 24, { align: "right" });
}

function drawFooter(doc, pageNum, pageCount) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.2);
  doc.line(12, h - 12, w - 12, h - 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("Documento clinico - Uso interno", 12, h - 7);
  doc.text(`Pagina ${pageNum} de ${pageCount}`, w - 12, h - 7, { align: "right" });
}

function ensureSpace(doc, y, needed, logoDataUrl, subtitle) {
  const h = doc.internal.pageSize.getHeight();
  if (y + needed > h - 18) {
    doc.addPage();
    drawHeader(doc, logoDataUrl, subtitle);
    return 36;
  }
  return y;
}

// Capture an in-page DOM node as a PNG dataURL. Returns null on failure.
async function captureNode(node) {
  if (!node) return null;
  try {
    // pixelRatio 2 gives crisp prints; backgroundColor avoids transparent SVG
    return await toPng(node, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#FFFFFF",
      skipFonts: true,
    });
  } catch (e) {
    console.warn("chart capture failed", e);
    return null;
  }
}

// Insert a captured chart image into the PDF, preserving its aspect ratio.
function placeImage(doc, dataUrl, x, y, maxW, maxH) {
  if (!dataUrl) return y;
  try {
    const props = doc.getImageProperties(dataUrl);
    const ratio = props.width / props.height;
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }
    doc.addImage(dataUrl, "PNG", x, y, w, h, undefined, "FAST");
    return y + h;
  } catch (e) {
    console.warn("placeImage failed", e);
    return y;
  }
}

function sectionTitle(doc, title, subtitle, y) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.blue);
  doc.circle(16, y, 3.5, "F");
  doc.setTextColor(...BRAND.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("=", 16, y + 1.4, { align: "center" });
  doc.setTextColor(...BRAND.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(title, 22, y + 1.5);
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.3);
  const tw = doc.getTextWidth(title);
  doc.line(22 + tw + 3, y + 1, w - 12, y + 1);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...BRAND.muted);
    doc.text(subtitle, 22, y + 6);
    return y + 11;
  }
  return y + 7;
}

const CHART_SPECS = [
  { id: "chart-ta", title: "Tension arterial", subtitle: "Normal sistolica 90-140 / diastolica 60-90 mmHg" },
  { id: "chart-fc", title: "Frecuencia cardiaca", subtitle: "Normal 60-100 lpm" },
  { id: "chart-temp", title: "Temperatura corporal", subtitle: "Normal 36-37.4 / Fiebre >=38 C" },
  { id: "chart-spo2", title: "Saturacion de oxigeno (SpO2)", subtitle: "Normal >=95% / Critico <90%" },
  { id: "chart-eva", title: "Dolor (EVA 0-10)", subtitle: "Banda roja 7-10 intenso" },
  { id: "chart-balance", title: "Balance hidrico", subtitle: "Banda verde objetivo 0 +/- 500 ml" },
  { id: "chart-sleep", title: "Horas de sueno", subtitle: "Rango recomendado adulto mayor 6-9 h/dia" },
  { id: "chart-morse", title: "Riesgo de caidas (Morse)", subtitle: "0-24 bajo / 25-44 medio / >=45 alto" },
];

const RANGE_LABELS = {
  "7d": "Ultimos 7 dias",
  "4w": "Ultimas 4 semanas",
  "3m": "Ultimos 3 meses",
  all: "Todo el historial",
};

const GROUP_LABELS = {
  day: "Por dia",
  week: "Por semana",
};

export async function exportAnalysisPDF({
  patient,
  rangeKey,
  groupKey,
  summary,
  pointsCount,
  materials = [],
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const subtitle = "Analisis clinico del paciente";
  const logoDataUrl = await loadLogo();
  const w = doc.internal.pageSize.getWidth();
  const left = 12;
  const right = w - 12;
  const contentW = right - left;

  drawHeader(doc, logoDataUrl, subtitle);
  let y = 36;

  // Patient + period
  y = sectionTitle(doc, "Informacion del reporte", null, y);
  doc.setFillColor(...BRAND.light);
  doc.roundedRect(left, y, contentW, 24, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("PACIENTE", left + 3, y + 5);
  doc.text("PERIODO", left + contentW / 2, y + 5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BRAND.ink);
  doc.text(patient || "-", left + 3, y + 12);
  doc.setFontSize(11);
  doc.text(RANGE_LABELS[rangeKey] || rangeKey, left + contentW / 2, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(
    summary.totalShifts
      ? `Del ${summary.firstDate} al ${summary.lastDate}`
      : "Sin turnos en el rango",
    left + contentW / 2,
    y + 18
  );
  doc.text(`Agrupacion: ${GROUP_LABELS[groupKey] || groupKey}`, left + 3, y + 21);
  y += 28;

  // Summary table
  y = sectionTitle(doc, "Resumen del periodo", `Total de turnos: ${pointsCount}`, y);
  autoTable(doc, {
    startY: y,
    head: [["Prioridad clinica", "Turnos"]],
    body: [
      ["Critico (rojo)", summary.priorityCounts.red || 0],
      ["Seguimiento (amarillo)", summary.priorityCounts.yellow || 0],
      ["Estable (verde)", summary.priorityCounts.green || 0],
      ["Sin cierre (gris)", summary.priorityCounts.gray || 0],
    ],
    theme: "grid",
    headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: BRAND.ink },
    columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
    margin: { left, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 4;

  if (summary.nurses && summary.nurses.length) {
    const nurseLines = doc.splitTextToSize(
      `Enfermeras involucradas: ${summary.nurses.join(", ")}`,
      contentW
    );
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    doc.text(nurseLines, left, y);
    y += nurseLines.length * 4 + 2;
  }

  // Material consumption in the selected period
  if (materials.length > 0) {
    y = ensureSpace(doc, y, 30, logoDataUrl, subtitle);
    y = sectionTitle(doc, "Consumo de material", `${materials.length} insumo(s) distintos en el periodo`, y);
    autoTable(doc, {
      startY: y,
      head: [["Material", "Cantidad", "Unidad", "Turnos"]],
      body: materials.map((m) => [m.name, m.quantity, m.unit, m.shifts]),
      theme: "grid",
      headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: BRAND.ink },
      columnStyles: {
        1: { halign: "right", fontStyle: "bold", cellWidth: 24 },
        2: { cellWidth: 22 },
        3: { halign: "right", cellWidth: 20 },
      },
      margin: { left, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  if (pointsCount === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.muted);
    doc.text(
      "No hay datos clinicos en el rango seleccionado para generar graficas.",
      left,
      y + 6
    );
  } else {
    // Capture each chart
    y = ensureSpace(doc, y, 30, logoDataUrl, subtitle);
    y = sectionTitle(doc, "Tendencias clinicas", null, y);

    for (const spec of CHART_SPECS) {
      const node = document.querySelector(`[data-testid="${spec.id}"]`);
      if (!node) continue;
      // Reserve space for title (8) + chart (~70) + spacing (5)
      y = ensureSpace(doc, y, 80, logoDataUrl, subtitle);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BRAND.ink);
      doc.text(spec.title, left, y);
      if (spec.subtitle) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...BRAND.muted);
        doc.text(spec.subtitle, left, y + 4);
      }
      y += 6;
      const png = await captureNode(node);
      if (png) {
        y = placeImage(doc, png, left, y, contentW, 75);
        y += 5;
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.muted);
        doc.text("(grafica no disponible)", left, y);
        y += 8;
      }
    }
  }

  // Footer pagination
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, pages);
  }

  // Filename
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(
    now.getDate()
  ).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
  const safePatient = (patient || "paciente").replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
  doc.save(`analisis_${safePatient}_${stamp}.pdf`);
}
